import {
  Component,
  computed,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject
} from '@angular/core';

import { DatePipe } from '@angular/common';
import { FileLoader } from '../../services/document-viewer/document-viewer.service';
import { debounceTime, fromEvent, Subscription, switchMap } from 'rxjs';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { Divider } from 'primeng/divider';
import { TranslatePipe } from '@ngx-translate/core';
import { buildContentUrl } from '../../utils/documentUtils';

@Component({
  selector: 'app-document-viewer',
  templateUrl: './document-viewer.component.html',
  styleUrl: './document-viewer.component.scss',
  imports: [Bind, Button, Tooltip, Divider, TranslatePipe, DatePipe],
  providers: [FileLoader]
})
export class DocumentViewerComponent implements OnChanges, OnDestroy {
  private fileLoader = inject(FileLoader);
  private featureFlagService = inject(FeatureFlagService);

  @Input() base64Content: string | undefined;
  @Input() isProcessingTranscript = true;
  @Input() isInTranscriptUpload = false;
  @Input() ecmDocId: number | null | undefined;
  @Input() expirationDate: string | null | undefined;
  @Output() unsupportedFileType = new EventEmitter<boolean>();
  @Output() startClassification = new EventEmitter<boolean>();
  @Output() openDocumentSplitEvent = new EventEmitter();

  readonly hasAnnotations = computed(() => this.fileLoader.hasAnnotations());
  readonly showAnnotations = computed(() => this.fileLoader.annotationsVisible());

  sendFromContentFlag = toSignal(
    this.featureFlagService.getFlag('feature-itp-send-from-content', false)
  );

  docExpirationUiEnabled = toSignal(
    this.featureFlagService.getFlag('feature-capture-doc-expiration-ui', false)
  );

  constructor() {
    fromEvent(window, 'resize')
      .pipe(debounceTime(300))
      .subscribe(() => {
        this.fileLoader.refreshView();
      });
  }

  /**
   *Get Current Page
   */
  get currentPage(): number {
    return this.fileLoader.currentPage();
  }

  /**
   * Get Total Pages
   */
  get totalPages(): number {
    return this.fileLoader.numPages();
  }

  /**
   * Disable the split button if its not a PDF or if there is only one page
   */
  get disableSplitButton(): boolean {
    return !this.base64Content || this.base64Content.charAt(0) !== 'J' || this.totalPages <= 1;
  }

  /**
   * Check if document has a valid ecmDocId
   */
  get hasEcmDocId(): boolean {
    return this.ecmDocId !== undefined && this.ecmDocId !== null;
  }

  /**
   * Get tooltip text for split button
   */
  get splitButtonTooltip(): string {
    if (!this.base64Content || this.base64Content.charAt(0) !== 'J') {
      return 'documentToolbar.splitDisabledDocType';
    }
    if (this.totalPages <= 1) {
      return 'documentToolbar.splitDisabledPageCount';
    }
    return 'documentToolbar.split';
  }

  /**
   * Handles changes to input properties.
   * @param changes An object of changed input properties.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['base64Content']) {
      this.fileLoader.annotationsVisible.set(!this.isInTranscriptUpload);
      if (this.base64Content) {
        if (!this.fileLoader.loadFile(this.base64Content)) {
          this.unsupportedFileType.emit(true);
        }
      } else {
        this.fileLoader.clearMap();
      }
    }
  }

  /**
   * Ng on Destroy
   */
  ngOnDestroy(): void {
    this.fileLoader.destroyMap();
  }

  /**
   * Go to Next Page
   */
  public nextPage() {
    if (this.base64Content) {
      this.fileLoader.nextPage(this.base64Content);
    }
  }

  /**
   * Go to previous page
   */
  public previousPage() {
    if (this.base64Content) {
      this.fileLoader.previousPage(this.base64Content);
    }
  }

  /**
   *zoomIn
   */
  public zoomIn() {
    this.fileLoader.zoomIn();
  }

  /**
   *zoomOut
   */
  public zoomOut() {
    this.fileLoader.zoomOut();
  }

  /**
   *recenter
   */
  public recenter() {
    this.fileLoader.recenter();
  }

  /**
   *rotate
   */
  public rotate() {
    this.fileLoader.rotate();
  }

  /**
   * Draws a red bounding box around the specified vertices on the document
   * @param vertices Array of four {x,y} coordinates that form the bounding box
   * @param page The page number where the box should be drawn (1-based)
   * @param color Optional color for the box (default: 'red')
   */
  drawBoundingBox(vertices: { x: number; y: number }[], page: number, color: string = 'red'): void {
    this.fileLoader.drawBoundingBox(vertices, page, color);
  }

  /**
   * Clears all highlight boxes
   */
  clearHighlightBoxes(): void {
    this.fileLoader.clearHighlightBoxes();
  }

  /**
   * Navigates to the specified page number in the document
   * @param pageNumber The page number to navigate to
   * @returns A promise that resolves when the navigation is complete
   */
  navigateToPage(pageNumber: number): Promise<void> {
    if (pageNumber <= 0) {
      return Promise.resolve();
    }

    return new Promise<void>(resolve => {
      if (this.base64Content) {
        this.fileLoader.goToPage(this.base64Content, pageNumber);
        setTimeout(() => {
          resolve();
        }, 500); // small hold to let document render before bounding box
      } else {
        resolve();
      }
    });
  }

  /**
   * Opens the split document feature
   */
  onSplitClick(): void {
    this.openDocumentSplitEvent.emit();
  }

  /**
   * Toggles the visibility of annotations on the document
   */
  toggleAnnotations() {
    this.fileLoader.setAnnotationsVisible(!this.showAnnotations());
  }

  /**
   * Opens the document in the content viewer in a new tab
   */
  goToDocument(): void {
    const documentUrl = buildContentUrl(this.ecmDocId);
    if (!documentUrl) {
      console.error('[DocumentViewerComponent] Content URL not found in localStorage');
      return;
    }

    window.open(documentUrl, '_blank', 'noopener,noreferrer');
  }
}
